import React from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import Linkify from 'react-linkify'
import {getYoutubeId, addEmoji, addStickers} from 'core/utils'
import {PENDING_MESSAGE_ID} from 'core/constants'
import {isNotSentId} from 'core/helpers'
import {Avatar, Time} from 'components/misc'
import {ccpProvider} from 'core/CcpProvider';
import {useFeatures} from 'core/hooks';
import UpgradeLink from '../UpgradeLink';
import {useState, useEffect} from 'react';
import {HiddenMessageDialog} from './HiddenMessageDialog';
export const MessageBasic = ({children, time, user, sent, pending, failed, type, shouldShowTimeStamp}) => (
  <div className={classnames('message-wrapper', {sent, received: !sent, pending, failed})}>
    <Avatar user={user} isActive/>
    <span className="identity-span">{user.username}</span>
    <div className={`chat-message ${type || ''}`}>{children}</div>
    {shouldShowTimeStamp && <span className="time-span">{failed ? 'not sent' : time}</span>}
  </div>
)

const parseAnchors = (text, stripLinks) => {
  const isAnchor = new RegExp(/(<a[\s]+[^>]+>(?:.(?!<\/a>))*.<\/a>)/g)
  const getAnchorVars = /<a[^>]+href="(.*?)"[^>]*>(.*?)<\/a>/g
  const textData = text.split(isAnchor)
  return textData.map(x => {
    if (isAnchor.test(x)) {
      if (stripLinks) {
        return ''
      }
      const [, href, content] = x.split(getAnchorVars)
      return {href, content}
    }
    return x
  })
}

const MessageDefault = ({message, isCorrespondent, users, user, shouldShowTimeStamp = true, conversation,  options}) => {
  const { showUploadButton } = options
  const props = {
    time: <Time since={message.sentAt}/>,
    user: isCorrespondent ? users.me : users.other,
    sent: isCorrespondent,
    pending: message.id === PENDING_MESSAGE_ID,
    failed: isNotSentId(message.id),
    hidden: message.hidden,
    shouldShowTimeStamp,
  }
  if (/^:sticker-\w+:$/.test(message.message)) {
    return <MessageBasic {...props} type="sticker-message">{addStickers(message.message)}</MessageBasic>
  }
  let {isCreditSite, hasFreeCredits} = useFeatures()
  if (isCreditSite === 0) {
    isCreditSite = false
  }
  const [isPremium ] = useState(user?.premium || false);
  const [isMessageLocked, setIsMessageLocked] = useState(message?.hidden || false);
  const [shouldShowUploadButton, setShouldShowUploadButton] = useState(showUploadButton);
  useEffect(() => {
    setIsMessageLocked(message?.hidden || false);
  }, [message]);

  useEffect(() => {
    const onHide = () => setShouldShowUploadButton(false)
    document.addEventListener('ccp:hide-upload-button', onHide)
    const onShow = () => setShouldShowUploadButton(true)
    document.addEventListener('ccp:show-upload-button', onShow)
  }, [])
  const messageRef = React.useRef(null);
  useEffect(() => {
    if (!messageRef.current) {
      return;
    }
    const el = messageRef.current.querySelector('.wrapper-image-cta');
    if (!el) {
      return;
    }
    const handler = () => {
      window.dispatchEvent(new CustomEvent('ccp:play-profile-video', {
        detail: { messageId: message.id },
      }));
    };
    el.addEventListener('click', handler);
    return () => {
      el.removeEventListener('click', handler);
    };
  }, [message.id]);
  const text = parseAnchors(message.message || '', message.sentBy !== 0)
  const MyLink = ({href}) => {
    if (href.match(/youtu(\.?be\/.|be.com\/)/)) {
      return <iframe className="chat-iframe" src={`//youtube.com/embed/${getYoutubeId(href)}`} frameBorder="0" allowFullScreen width="200" height="160"></iframe>
    }
    if (href.match(/\.(gif)/i)) {
      return <img src={href} className="chat-img"/>
    } else if (href.match(/\.(jpe?g|png|bmp|tiff|webp|bpg|heif)/i)) {
      return (
        <a href={href} className="ccp-chat-link"><img src={href} className="chat-img"/></a>
      )
    } else if (href.match(/mailto:.+/i)) {
      return (
        <a href={href}>{href.replace('mailto:', '')}</a>
      )
    }

    return message.sentBy === 0 ? href : ''
  }
  const PhotoRequestUI = () => (
    <div className="profile-photo-request">
      <span>Requested a photo of you</span>
      {shouldShowUploadButton &&
        <button onClick={options.hooks?.onUploadPhotoRequest} className="btn upload-photo__btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="17" viewBox="0 0 96 80" fill="white">
            <path
              d="M72 20h-8.586l-4.707-4.707A4 4 0 0 0 56 14H40a4 4 0 0 0-2.707 1.293L32.586 20H24a8 8 0 0 0-8 8v32a8 8 0 0 0 8 8h48a8 8 0 0 0 8-8V28a8 8 0 0 0-8-8zM48 60a16 16 0 1 1 0-32 16 16 0 0 1 0 32zm0-6a10 10 0 1 0 0-20 10 10 0 0 0 0 20z"/>
          </svg>
          SET PROFILE PHOTO
        </button>
      }
    </div>
  )

  const unlockMessage = async e => {
    e.preventDefault()
    const [res, err] = await ccpProvider.api.user.unlockMessage({messageId: message.id})
    if (!err && res.success) {
      options.hooks?.onRevealMessage?.()
      conversation.messages.find(({id}) => id === message.id).hidden = false
      setIsMessageLocked(false)
    }
  }
  return (
    <MessageBasic {...props}>
      <div ref={messageRef}>
      {isMessageLocked && (isPremium || hasFreeCredits) && isCreditSite &&
        <a
          href="#"
          className="upgrade-link no-under unlock-message-widget"
          onClick={unlockMessage}>
          <p>Unlock this message</p>
        </a>
      }
      {isMessageLocked && !isPremium &&
        <HiddenMessageDialog message={message} isCreditSite={isCreditSite} conversation={conversation} />
      }
      <Linkify component={MyLink} className={isMessageLocked && ((!isPremium && !isCreditSite) || isCreditSite)
        ? 'blurred-message'
        : ''}>
        {text.map((x, i) => {
          if (typeof x === 'string') {
            if (x === 'PhotoRequestMessage') {
              return <PhotoRequestUI key={`photo-request-${i}`}/>
            }
            return addEmoji(x, { useImages: true })
          }
          return (
            <a
              key={i}
              href={x.href}
              dangerouslySetInnerHTML={{ __html: x.content }}
            />
          )
        })}
      </Linkify>
      </div>
    </MessageBasic>
  )
}

MessageDefault.propTypes = {
  isCorrespondent: PropTypes.bool.isRequired,
  message: PropTypes.shape({
    id: PropTypes.number,
    data: PropTypes.string,
    sentAt: PropTypes.number,
    hidden: PropTypes.bool,
  }).isRequired,
  options: PropTypes.object,
}

export default MessageDefault
